import { Link } from 'react-router-dom'
import { ArrowDown, ArrowUp, Bell, CalendarClock, CheckSquare, ExternalLink, Mail, MessageSquare, StickyNote, Trash2 } from 'lucide-react'
import Select from '@/components/ui/Select'
import RichText from '@/components/RichText'
import RichTextEditor from '@/components/RichTextEditor'
import { ACTION_TYPES, newAction } from '@/features/workflows/workflowMeta'
import type { Action } from '@/api/workflows'
import type { MessageTemplate } from '@/api/messages'
import type { Member } from '@/api/users'

const inputCls =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

const ICONS: Record<string, React.ReactNode> = {
  send_email: <Mail size={16} />,
  send_sms: <MessageSquare size={16} />,
  notify: <Bell size={16} />,
  create_task: <CheckSquare size={16} />,
  add_note: <StickyNote size={16} />,
  set_next_contact_date: <CalendarClock size={16} />,
}

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  )
}

/** Who a notification or task goes to: the record's owner or a named teammate. */
function PersonSelect({ value, onChange, members, label }: { value: unknown; onChange: (v: string) => void; members: Member[]; label: string }) {
  return (
    <Select size="md" aria-label={label} value={String(value ?? 'owner')} onChange={(e) => onChange(e.target.value)} className="w-full">
      <option value="owner">The deal’s owner</option>
      {members.map((m) => <option key={m.id} value={m.id}>{m.fullName}</option>)}
    </Select>
  )
}

/**
 * One step of a workflow as a card: what it does, its settings, and — for messages — exactly which
 * template goes out with a preview and a link to edit it, so "which email is sent, and where do I change
 * it?" is answered on the spot.
 */
export default function ActionStep({
  index,
  action,
  onChange,
  onRemove,
  onMove,
  isFirst,
  isLast,
  templates,
  members,
  contactTrigger,
}: {
  index: number
  action: Action
  onChange: (a: Action) => void
  onRemove: () => void
  onMove: (dir: -1 | 1) => void
  isFirst: boolean
  isLast: boolean
  templates: MessageTemplate[]
  members: Member[]
  contactTrigger: boolean
}) {
  const t = action.type
  const set = (patch: Partial<Action>) => onChange({ ...action, ...patch })
  const channel = t === 'send_sms' ? 'sms' : 'email'
  const channelTemplates = templates.filter((x) => x.channel === channel)
  const template = action.templateId ? templates.find((x) => x.id === action.templateId) : undefined
  const custom = (t === 'send_email' || t === 'send_sms') && !action.templateId && action.templateId !== ''
  const types = ACTION_TYPES.filter((x) => !contactTrigger || x.contact)

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
        <span className="w-8 h-8 rounded-lg bg-brand-primary/10 text-brand-primary flex items-center justify-center shrink-0">{ICONS[t] ?? index + 1}</span>
        <span className="text-xs font-semibold text-gray-400 tracking-wider">STEP {index + 1}</span>
        <Select size="md" aria-label={`Step ${index + 1} action`} value={t} onChange={(e) => onChange({ ...newAction(e.target.value), delayDays: action.delayDays, cancelOnStageChange: action.cancelOnStageChange })} className="flex-1 min-w-0">
          {types.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
        </Select>
        <div className="flex items-center gap-0.5 shrink-0">
          <button type="button" onClick={() => onMove(-1)} disabled={isFirst} className="p-1.5 text-gray-400 hover:text-brand-primary disabled:opacity-30" title="Move up"><ArrowUp size={15} /></button>
          <button type="button" onClick={() => onMove(1)} disabled={isLast} className="p-1.5 text-gray-400 hover:text-brand-primary disabled:opacity-30" title="Move down"><ArrowDown size={15} /></button>
          <button type="button" onClick={onRemove} className="p-1.5 text-gray-400 hover:text-danger" title="Remove step"><Trash2 size={15} /></button>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {(t === 'send_email' || t === 'send_sms') && (
          <>
            <div className="flex gap-4 text-sm text-gray-700" role="radiogroup" aria-label="Message content">
              <label className="flex items-center gap-1.5">
                <input type="radio" checked={!custom} onChange={() => set({ templateId: channelTemplates[0]?.id ?? '', subject: undefined, body: undefined })} />
                Use a template
              </label>
              <label className="flex items-center gap-1.5">
                <input type="radio" checked={custom} onChange={() => set({ templateId: undefined, subject: t === 'send_email' ? '' : undefined, body: '' })} />
                Write it here
              </label>
            </div>
            {!custom ? (
              <>
                <Select size="md" aria-label="Template" value={String(action.templateId ?? '')} onChange={(e) => set({ templateId: e.target.value })} className="w-full">
                  <option value="">Choose a template…</option>
                  {channelTemplates.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </Select>
                {template && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
                      <span className="text-xs text-gray-500">
                        {t === 'send_email' ? <>Subject: <span className="font-medium text-gray-700">{template.subject}</span></> : 'Message'}
                      </span>
                      <Link to="/settings?tab=templates" className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:underline">
                        Edit template <ExternalLink size={11} />
                      </Link>
                    </div>
                    <div className="px-3 py-2.5 bg-white max-h-48 overflow-y-auto">
                      {t === 'send_email' ? (
                        <RichText html={template.body.replace(/\n/g, '<br>')} className="text-sm text-gray-700" />
                      ) : (
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{template.body}</p>
                      )}
                    </div>
                  </div>
                )}
                <p className="text-xs text-gray-400">
                  Goes from the deal’s owner, with their signature, to the deal’s main contact. Tokens like {'{{first_name}}'} are
                  filled in for each client. Contacts who opted out are skipped.
                </p>
              </>
            ) : t === 'send_email' ? (
              <>
                <Field label="Subject">
                  <input className={inputCls} value={String(action.subject ?? '')} onChange={(e) => set({ subject: e.target.value })} placeholder="e.g. Congratulations, {{first_name}}!" />
                </Field>
                <Field label="Email">
                  <RichTextEditor value={String(action.body ?? '')} onChange={(html) => set({ body: html })} placeholder="Hi {{first_name}}, …" />
                </Field>
              </>
            ) : (
              <Field label="Text message">
                <textarea className={inputCls} rows={3} value={String(action.body ?? '')} onChange={(e) => set({ body: e.target.value })} placeholder="Hi {{first_name}}, …" />
              </Field>
            )}
          </>
        )}

        {t === 'notify' && (
          <>
            <Field label="Who to notify">
              <PersonSelect value={action.to} onChange={(v) => set({ to: v })} members={members} label="Who to notify" />
            </Field>
            <Field label="Message" hint="They get it by email with a link to the deal; it’s also noted on the deal’s timeline.">
              <textarea className={inputCls} rows={3} value={String(action.message ?? '')} onChange={(e) => set({ message: e.target.value })} placeholder="e.g. A new VIP enquiry — please call within the hour." />
            </Field>
          </>
        )}

        {t === 'create_task' && (
          <div className="grid gap-3 sm:grid-cols-[1fr_8rem]">
            <Field label="Task">
              <input className={inputCls} value={String(action.title ?? '')} onChange={(e) => set({ title: e.target.value })} placeholder="e.g. Call the new lead" />
            </Field>
            <Field label="Due in (days)">
              <input className={inputCls} type="number" min={0} value={action.dueInDays == null ? '' : String(action.dueInDays)} onChange={(e) => set({ dueInDays: e.target.value === '' ? undefined : Number(e.target.value) })} />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Assign to">
                <PersonSelect value={action.assignee} onChange={(v) => set({ assignee: v })} members={members} label="Assign to" />
              </Field>
            </div>
          </div>
        )}

        {t === 'add_note' && (
          <Field label="Note">
            <textarea className={inputCls} rows={2} value={String(action.body ?? '')} onChange={(e) => set({ body: e.target.value })} placeholder="e.g. Sent the just-sold pack automatically." />
          </Field>
        )}

        {t === 'set_next_contact_date' && (
          <Field label="Days from today">
            <input className={`${inputCls} max-w-32`} type="number" min={0} value={action.inDays == null ? '' : String(action.inDays)} onChange={(e) => set({ inDays: e.target.value === '' ? undefined : Number(e.target.value) })} />
          </Field>
        )}

        {!contactTrigger && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-2 border-t border-gray-100 text-sm text-gray-600">
            <label className="flex items-center gap-1.5">
              Wait
              <input
                type="number"
                min={0}
                aria-label="Days to wait before this step"
                value={action.delayDays == null ? '' : String(action.delayDays)}
                placeholder="0"
                onChange={(e) => {
                  const days = e.target.value === '' ? 0 : Math.max(0, Math.floor(Number(e.target.value)))
                  const next = { ...action }
                  if (!days) {
                    delete next.delayDays
                    delete next.cancelOnStageChange
                  } else next.delayDays = days
                  onChange(next)
                }}
                className="w-16 px-2 py-1 border border-gray-200 rounded-lg text-sm"
              />
              days before this step
            </label>
            {Number(action.delayDays ?? 0) > 0 && (
              <label className="flex items-center gap-1.5">
                <input type="checkbox" checked={!!action.cancelOnStageChange} onChange={(e) => set({ cancelOnStageChange: e.target.checked })} />
                Skip it if the deal has moved stage by then
              </label>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
